import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { Box, Button, Modal, Typography } from "@mui/material";
import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

type StackName = string;
type StackMap = Record<StackName, string[]>;
type StackCapacityMap = Record<StackName, number>;

export interface TowerConfig {
  training: boolean;
  is_random: boolean;
  stacks: Array<{
    initial: string[] | null;
    target: string[] | null;
    stack_length: number;
  }>;
  balls: string[];
  min_moves: number;
  round_position?: string | boolean;
  between_rounds_ms?: number;
  unlock_ms?: number;
  feedback_text?: string;
  introduction?: {
    title?: string;
    text?: string;
    instructions?: string[];
  };
  training_finish?: {
    title?: string;
    text?: string;
  };
}

interface ResolvedTowerStackConfig {
  initial: string[];
  target: string[];
  stack_length: number;
}

interface ResolvedTowerConfig extends Omit<TowerConfig, "stacks"> {
  stacks: ResolvedTowerStackConfig[];
  between_rounds_ms: number;
  unlock_ms: number;
  feedback_text: string;
  disc_color_map: Record<string, string>;
}

interface TowerMove {
  from_stack: StackName;
  disc: string;
  to_stack: StackName;
  total_time: number;
}

export interface TowerTaskAnswer {
  target_stack: StackMap;
  user_stack: StackMap;
  user_moves: Record<string, TowerMove>;
}

interface Props {
  onAnswerChange: (taskAnswer?: TowerTaskAnswer) => void;
  towerConfig?: Partial<TowerConfig>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BASE_DISC_PALETTE: Array<{ name: string; color: string }> = [
  { name: "red", color: "#e53935" },
  { name: "green", color: "#43a047" },
  { name: "blue", color: "#1e88e5" },
  { name: "purple", color: "#8e24aa" },
  { name: "brown", color: "#6d4c41" },
  { name: "orange", color: "#f4511e" },
  { name: "turquoise", color: "#00897b" },
  { name: "yellow", color: "#fdd835" },
];

const DEFAULT_CONFIG: TowerConfig = {
  training: true,
  is_random: true,
  stacks: [
    { initial: null, target: null, stack_length: 3 },
    { initial: null, target: null, stack_length: 2 },
    { initial: null, target: null, stack_length: 1 },
  ],
  balls: ["red", "green", "blue"],
  min_moves: 3,
  round_position: "middle",
  between_rounds_ms: 3000,
  unlock_ms: 3000,
  feedback_text: "Rodada concluída!",
};
const INTRO_COUNTDOWN_SECONDS = 3;
const TIMER_TICK_MS = 100;
const DISC_DIAMETER = 42;
const DISC_GAP = 2;
const ROD_BOTTOM_OFFSET = 12;
const ROD_TOP_PADDING = 12;
const INTRODUCTION_TITLE = "Como funciona o desafio das torres";
const INTRODUCTION_TEXT =
  "Nesta tarefa, você verá alguns pinos e algumas bolas coloridas. Seu objetivo é organizar as bolas para que elas fiquem exatamente na mesma posição da configuração alvo mostrado na tela. Antes de movimentar as bolas, planeje mentalmente o passo que você vai seguir, e tente completar cada rodada com o menor número possível de movimentos.";
const DEFAULT_INTRODUCTION_INSTRUCTIONS = [
  "Você só pode movimentar uma bola de cada vez.",
  "Só é possível movimentar a bola que está no topo de um pino.",
  "Se uma bola estiver embaixo da outra, você precisa primeiro movimentar a bola de cima para outro pino antes de movimentar a de baixo.",
  "Na fase de treino, os pinos têm tamanhos iguais e há somente duas bolas. Movimente as bolas para que elas fiquem na mesma posição do modelo. Lembre-se de planejar mentalmente os movimentos antes de executá-los.",
];
const START_BUTTON_LABEL = "Começar desafio";
const DEFAULT_TRAINING_FINISH_TITLE = "Treino concluído!";
const DEFAULT_TRAINING_FINISH_TEXT =
  "Agora que você já praticou, vamos continuar a tarefa. Movimente as bolas entre os pinos até alcançar o modelo apresentado na configuração alvo. Lembre-se que é muito importante que você planeje os movimentos antes de executá-los e lembre-se que você só poderá movimentar uma bola de cada vez.";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ── Config resolution ─────────────────────────────────────────────────────
const toSafeInt = (
  value: number | undefined,
  fallback: number,
  min: number
): number => {
  if (!Number.isFinite(value)) {
    return Math.max(min, Math.floor(fallback));
  }

  const parsedValue = Math.floor(Number(value));
  return Math.max(min, parsedValue);
};

const normalizeToken = (value: string): string => value.trim().toLowerCase();

const normalizeBallList = (rawValue: unknown): string[] => {
  if (!Array.isArray(rawValue)) {
    return [];
  }

  return rawValue
    .filter((value): value is string => typeof value === "string")
    .map((value) => normalizeToken(value))
    .filter((value) => value.length > 0);
};

const buildBallColorMap = (ballTokens: string[]): Record<string, string> => {
  const uniqueBallTokens = Array.from(new Set(ballTokens));

  const paletteByName = new Map<string, string>();
  const paletteByColor = new Map<string, string>();
  BASE_DISC_PALETTE.forEach((paletteItem) => {
    paletteByName.set(normalizeToken(paletteItem.name), paletteItem.color);
    paletteByColor.set(normalizeToken(paletteItem.color), paletteItem.name);
  });

  const discColorMap: Record<string, string> = {};
  uniqueBallTokens.forEach((ballToken) => {
    const normalizedBall = normalizeToken(ballToken);
    const predefinedColorByName = paletteByName.get(normalizedBall);
    if (predefinedColorByName) {
      discColorMap[ballToken] = predefinedColorByName;
      return;
    }

    if (paletteByColor.has(normalizedBall)) {
      discColorMap[ballToken] = ballToken;
      return;
    }

    const isRawColor =
      normalizedBall.startsWith("#") ||
      normalizedBall.startsWith("rgb(") ||
      normalizedBall.startsWith("hsl(");

    if (isRawColor) {
      discColorMap[ballToken] = ballToken;
      return;
    }

    discColorMap[ballToken] = ballToken;
  });

  return discColorMap;
};

const resolveTowerConfig = (partialConfig?: Partial<TowerConfig>): ResolvedTowerConfig => {
  const baseConfig = {
    ...DEFAULT_CONFIG,
    ...partialConfig,
  };

  const configuredBalls = normalizeBallList(baseConfig.balls);
  const fallbackBalls = normalizeBallList(DEFAULT_CONFIG.balls);
  const balls = configuredBalls.length > 0 ? configuredBalls : fallbackBalls;

  const configuredStacks =
    Array.isArray(baseConfig.stacks) && baseConfig.stacks.length > 0
      ? baseConfig.stacks
      : DEFAULT_CONFIG.stacks;

  const stacks = configuredStacks.map((rawStack, index) => {
    const fallbackStack = DEFAULT_CONFIG.stacks[index % DEFAULT_CONFIG.stacks.length];
    const stackLength = toSafeInt(
      rawStack?.stack_length,
      fallbackStack?.stack_length ?? 1,
      1
    );

    const initial = normalizeBallList(rawStack?.initial).slice(0, stackLength);
    const target = normalizeBallList(rawStack?.target).slice(0, stackLength);

    return {
      stack_length: stackLength,
      initial,
      target,
    };
  });

  const stackDefinedBalls = stacks.flatMap((stack) => [...stack.initial, ...stack.target]);
  const colorMap = buildBallColorMap([...balls, ...stackDefinedBalls]);
  const betweenRoundsMs = toSafeInt(
    baseConfig.between_rounds_ms,
    DEFAULT_CONFIG.between_rounds_ms ?? 3000,
    0
  );
  const unlockMs = toSafeInt(
    baseConfig.unlock_ms,
    DEFAULT_CONFIG.unlock_ms ?? 3000,
    0
  );

  return {
    training: Boolean(baseConfig.training),
    is_random: baseConfig.is_random !== false,
    stacks,
    balls,
    min_moves: toSafeInt(baseConfig.min_moves, DEFAULT_CONFIG.min_moves, 0),
    round_position: baseConfig.round_position,
    between_rounds_ms: betweenRoundsMs,
    unlock_ms: unlockMs,
    feedback_text:
      typeof baseConfig.feedback_text === "string"
        ? baseConfig.feedback_text
        : DEFAULT_CONFIG.feedback_text ?? "",
    disc_color_map: colorMap,
  };
};

// ── Stack generation ──────────────────────────────────────────────────────
const buildStackNames = (stackQuantity: number): StackName[] =>
  Array.from({ length: stackQuantity }, (_, index) => `stack_${index + 1}`);

const buildStackCapacities = (
  stackConfigs: ResolvedTowerStackConfig[],
  stackNames: StackName[]
): StackCapacityMap => {
  const capacities: StackCapacityMap = {};

  stackNames.forEach((stackName, index) => {
    capacities[stackName] = toSafeInt(
      stackConfigs[index]?.stack_length,
      1,
      1
    );
  });

  return capacities;
};

const buildEmptyStacks = (stackNames: StackName[]): StackMap => {
  const stacks: StackMap = {};
  stackNames.forEach((stackName) => {
    stacks[stackName] = [];
  });
  return stacks;
};

const cloneStacks = (stacks: StackMap, stackNames: StackName[]): StackMap => {
  const clonedStacks: StackMap = {};
  stackNames.forEach((stackName) => {
    clonedStacks[stackName] = [...(stacks[stackName] || [])];
  });
  return clonedStacks;
};

const stateKey = (stacks: StackMap, stackNames: StackName[]): string =>
  stackNames.map((stackName) => stacks[stackName].join(",")).join("|");

const randomInteger = (maxExclusive: number): number =>
  Math.floor(Math.random() * maxExclusive);

const createRandomStacks = (
  discColors: string[],
  stackNames: StackName[],
  stackCapacities: StackCapacityMap
): StackMap => {
  const stacks = buildEmptyStacks(stackNames);

  discColors.forEach((discColor) => {
    const availableStacks = stackNames.filter(
      (stackName) => stacks[stackName].length < (stackCapacities[stackName] || 0)
    );
    if (availableStacks.length === 0) {
      return;
    }

    const selectedStackName = availableStacks[randomInteger(availableStacks.length)];
    stacks[selectedStackName] = [...stacks[selectedStackName], discColor];
  });

  return stacks;
};

interface CandidateMove {
  fromStack: StackName;
  toStack: StackName;
  disc: string;
}

const getValidMoves = (
  stacks: StackMap,
  stackNames: StackName[],
  stackCapacities: StackCapacityMap
): CandidateMove[] => {
  const validMoves: CandidateMove[] = [];

  stackNames.forEach((fromStackName) => {
    const sourceDiscs = stacks[fromStackName];
    if (sourceDiscs.length === 0) {
      return;
    }

    const movedDisc = sourceDiscs[sourceDiscs.length - 1];
    if (!movedDisc) {
      return;
    }

    stackNames.forEach((toStackName) => {
      if (toStackName === fromStackName) {
        return;
      }

      if (stacks[toStackName].length >= (stackCapacities[toStackName] || 0)) {
        return;
      }

      validMoves.push({
        fromStack: fromStackName,
        toStack: toStackName,
        disc: movedDisc,
      });
    });
  });

  return validMoves;
};

const applyMoveToStacks = (
  stacks: StackMap,
  move: CandidateMove,
  stackNames: StackName[]
): StackMap => {
  const nextStacks = cloneStacks(stacks, stackNames);
  nextStacks[move.fromStack] = nextStacks[move.fromStack].slice(0, -1);
  nextStacks[move.toStack] = [...nextStacks[move.toStack], move.disc];
  return nextStacks;
};

const getShortestMoveCount = (
  sourceStacks: StackMap,
  targetStacks: StackMap,
  stackNames: StackName[],
  stackCapacities: StackCapacityMap
): number => {
  const sourceKey = stateKey(sourceStacks, stackNames);
  const targetKey = stateKey(targetStacks, stackNames);

  if (sourceKey === targetKey) {
    return 0;
  }

  const queue: Array<{ stacks: StackMap; depth: number }> = [
    { stacks: cloneStacks(sourceStacks, stackNames), depth: 0 },
  ];
  const visited = new Set<string>([sourceKey]);

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      break;
    }

    const possibleMoves = getValidMoves(current.stacks, stackNames, stackCapacities);
    for (const move of possibleMoves) {
      const nextStacks = applyMoveToStacks(current.stacks, move, stackNames);
      const nextKey = stateKey(nextStacks, stackNames);

      if (visited.has(nextKey)) {
        continue;
      }

      if (nextKey === targetKey) {
        return current.depth + 1;
      }

      visited.add(nextKey);
      queue.push({ stacks: nextStacks, depth: current.depth + 1 });
    }
  }

  return 0;
};

const buildTaskAnswer = (
  targetStack: StackMap,
  userStack: StackMap,
  moves: TowerMove[],
  stackNames: StackName[]
): TowerTaskAnswer => {
  const userMoves: Record<string, TowerMove> = {};
  moves.forEach((move, index) => {
    userMoves[`move_${index + 1}`] = move;
  });

  return {
    target_stack: cloneStacks(targetStack, stackNames),
    user_stack: cloneStacks(userStack, stackNames),
    user_moves: userMoves,
  };
};

const createFallbackStacks = (
  discColors: string[],
  stackNames: StackName[],
  stackCapacities: StackCapacityMap
): { targetStack: StackMap; userStack: StackMap } => {
  const fallbackUserStack = buildEmptyStacks(stackNames);
  const fallbackTargetStack = buildEmptyStacks(stackNames);

  discColors.forEach((discColor) => {
    const firstAvailableStack = stackNames.find(
      (stackName) =>
        fallbackUserStack[stackName].length < (stackCapacities[stackName] || 0)
    );
    if (firstAvailableStack) {
      fallbackUserStack[firstAvailableStack] = [
        ...fallbackUserStack[firstAvailableStack],
        discColor,
      ];
    }
  });

  const reversedStackNames = [...stackNames].reverse();
  discColors.forEach((discColor) => {
    const firstAvailableStack = reversedStackNames.find(
      (stackName) =>
        fallbackTargetStack[stackName].length < (stackCapacities[stackName] || 0)
    );
    if (firstAvailableStack) {
      fallbackTargetStack[firstAvailableStack] = [
        ...fallbackTargetStack[firstAvailableStack],
        discColor,
      ];
    }
  });

  return {
    targetStack: fallbackTargetStack,
    userStack: fallbackUserStack,
  };
};

const createReachableRandomTarget = (
  sourceStacks: StackMap,
  config: ResolvedTowerConfig,
  stackNames: StackName[],
  stackCapacities: StackCapacityMap
): StackMap => {
  const desiredMinSteps = Math.max(config.min_moves, config.balls.length);
  const extraSteps = config.balls.length + stackNames.length;
  const randomSteps = desiredMinSteps + randomInteger(extraSteps + 1);

  let currentStacks = cloneStacks(sourceStacks, stackNames);
  let lastMove: CandidateMove | null = null;

  for (let step = 0; step < randomSteps; step += 1) {
    const allMoves = getValidMoves(
      currentStacks,
      stackNames,
      stackCapacities
    );

    if (allMoves.length === 0) {
      break;
    }

    const prevMove: CandidateMove | null = lastMove;
    const noImmediateUndoMoves: CandidateMove[] = allMoves.filter((move) => {
      if (!prevMove) {
        return true;
      }
      return !(
        move.fromStack === prevMove.toStack && move.toStack === prevMove.fromStack
      );
    });

    const movePool: CandidateMove[] = noImmediateUndoMoves.length > 0 ? noImmediateUndoMoves : allMoves;
    const selectedMove: CandidateMove = movePool[randomInteger(movePool.length)];

    currentStacks = applyMoveToStacks(currentStacks, selectedMove, stackNames);
    lastMove = selectedMove;
  }

  return currentStacks;
};

const generateInitialStacks = (config: ResolvedTowerConfig): {
  stackNames: StackName[];
  stackCapacities: StackCapacityMap;
  targetStack: StackMap;
  userStack: StackMap;
} => {
  const stackNames = buildStackNames(config.stacks.length);
  const stackCapacities = buildStackCapacities(config.stacks, stackNames);
  const balls = config.balls;

  const fallback = createFallbackStacks(balls, stackNames, stackCapacities);

  if (!config.is_random) {
    const customUserStack = buildEmptyStacks(stackNames);
    const customTargetStack = buildEmptyStacks(stackNames);

    stackNames.forEach((stackName, index) => {
      const stackConfig = config.stacks[index];
      const stackCapacity = stackCapacities[stackName] || 0;

      customUserStack[stackName] = [...(stackConfig?.initial || [])].slice(
        0,
        stackCapacity
      );
      customTargetStack[stackName] = [...(stackConfig?.target || [])].slice(
        0,
        stackCapacity
      );
    });

    return {
      stackNames,
      stackCapacities,
      targetStack: customTargetStack,
      userStack: customUserStack,
    };
  }

  for (let attempt = 0; attempt < 250; attempt += 1) {
    const userStack = createRandomStacks(
      balls,
      stackNames,
      stackCapacities
    );

    const targetStack = createReachableRandomTarget(
      userStack,
      config,
      stackNames,
      stackCapacities
    );

    const distance = getShortestMoveCount(
      userStack,
      targetStack,
      stackNames,
      stackCapacities
    );

    if (
      stateKey(userStack, stackNames) !== stateKey(targetStack, stackNames) &&
      distance >= config.min_moves
    ) {
      return {
        stackNames,
        stackCapacities,
        targetStack,
        userStack,
      };
    }
  }

  return {
    stackNames,
    stackCapacities,
    targetStack: fallback.targetStack,
    userStack: fallback.userStack,
  };
};

const DEFAULT_RESOLVED_CONFIG = resolveTowerConfig(DEFAULT_CONFIG);

const getStackHeightByCapacity = (stackCapacity: number): number => {
  const bottomOffset = 24;
  const safetyTopOffset = 20;
  const discsTotalHeight =
    stackCapacity * DISC_DIAMETER + Math.max(0, stackCapacity - 1) * DISC_GAP;
  return bottomOffset + discsTotalHeight + safetyTopOffset;
};

// ─── Component ────────────────────────────────────────────────────────────────

const TowerQuestion: React.FC<Props> = ({
  onAnswerChange,
  towerConfig,
}) => {
  // ── Derived config ────────────────────────────────────────────────────────────
  const resolvedConfig = React.useMemo(() => resolveTowerConfig(towerConfig), [towerConfig]);
  const shouldShowIntroduction = React.useMemo(() => {
    if (typeof resolvedConfig.round_position === "boolean") {
      return resolvedConfig.round_position;
    }

    if (typeof resolvedConfig.round_position === "string") {
      const normalizedRoundPosition = normalizeToken(resolvedConfig.round_position);
      return normalizedRoundPosition === "first" || normalizedRoundPosition === "true";
    }

    return true;
  }, [resolvedConfig.round_position]);
  const isLastRoundPosition = React.useMemo(
    () =>
      typeof resolvedConfig.round_position === "string" &&
      normalizeToken(resolvedConfig.round_position) === "last",
    [resolvedConfig.round_position]
  );
  const isTrainingLastPosition = React.useMemo(
    () =>
      typeof resolvedConfig.round_position === "string" &&
      normalizeToken(resolvedConfig.round_position) === "training_finish",
    [resolvedConfig.round_position]
  );

  // ── State ────────────────────────────────────────────────────────────────────
  const [stackNames, setStackNames] = React.useState<StackName[]>(() =>
    buildStackNames(DEFAULT_RESOLVED_CONFIG.stacks.length)
  );
  const [stackCapacities, setStackCapacities] = React.useState<StackCapacityMap>(
    () =>
      buildStackCapacities(
        DEFAULT_RESOLVED_CONFIG.stacks,
        buildStackNames(DEFAULT_RESOLVED_CONFIG.stacks.length)
      )
  );

  const [targetStack, setTargetStack] = React.useState<StackMap>(() =>
    buildEmptyStacks(buildStackNames(DEFAULT_RESOLVED_CONFIG.stacks.length))
  );

  const [userStack, setUserStack] = React.useState<StackMap>(() =>
    buildEmptyStacks(buildStackNames(DEFAULT_RESOLVED_CONFIG.stacks.length))
  );

  const [moves, setMoves] = React.useState<TowerMove[]>([]);
  const [selectedSourceStack, setSelectedSourceStack] = React.useState<StackName | null>(
    null
  );
  const [lastMoveTime, setLastMoveTime] = React.useState<number | null>(null);
  const [hasChallengeStarted, setHasChallengeStarted] = React.useState<boolean>(
    () => !shouldShowIntroduction
  );
  const [isChallengeCompleted, setIsChallengeCompleted] = React.useState<boolean>(
    false
  );
  const [isRoundLocked, setIsRoundLocked] = React.useState<boolean>(
    () => !shouldShowIntroduction
  );
  const [isStartingCountdown, setIsStartingCountdown] = React.useState<boolean>(
    false
  );
  const [countdownValue, setCountdownValue] = React.useState<number>(
    INTRO_COUNTDOWN_SECONDS
  );
  const [roundUnlockRemainingMs, setRoundUnlockRemainingMs] = React.useState<number>(
    DEFAULT_RESOLVED_CONFIG.unlock_ms
  );
  const [pendingFinalAnswer, setPendingFinalAnswer] = React.useState<TowerTaskAnswer | null>(
    null
  );
  const [isCompletionModalOpen, setIsCompletionModalOpen] = React.useState<boolean>(false);
  const [completionModalMode, setCompletionModalMode] = React.useState<
    "countdown" | "manual" | null
  >(null);
  const [completionRemainingMs, setCompletionRemainingMs] = React.useState<number>(
    DEFAULT_RESOLVED_CONFIG.between_rounds_ms
  );
  const [completionMoveCount, setCompletionMoveCount] = React.useState<number | null>(
    null
  );

  // ── Effects ───────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    const initialStacks = generateInitialStacks(resolvedConfig);

    setStackNames(initialStacks.stackNames);
    setStackCapacities(initialStacks.stackCapacities);
    setTargetStack(initialStacks.targetStack);
    setUserStack(initialStacks.userStack);
    setMoves([]);
    setSelectedSourceStack(null);
    setLastMoveTime(null);
    setHasChallengeStarted(!shouldShowIntroduction);
    setIsChallengeCompleted(false);
    setIsRoundLocked(!shouldShowIntroduction);
    setIsStartingCountdown(false);
    setCountdownValue(INTRO_COUNTDOWN_SECONDS);
    setRoundUnlockRemainingMs(resolvedConfig.unlock_ms);
    setPendingFinalAnswer(null);
    setIsCompletionModalOpen(false);
    setCompletionModalMode(null);
    setCompletionRemainingMs(resolvedConfig.between_rounds_ms);
    setCompletionMoveCount(null);
    onAnswerChange(undefined);
  }, [onAnswerChange, shouldShowIntroduction, resolvedConfig]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const canDropDisc = (
    sourceStackName: StackName | null,
    targetStackName: StackName
  ): boolean => {
    if (!hasChallengeStarted) {
      return false;
    }
    if (isChallengeCompleted) {
      return false;
    }
    if (isRoundLocked) {
      return false;
    }

    if (!sourceStackName) {
      return false;
    }

    if (sourceStackName === targetStackName) {
      return false;
    }

    if ((userStack[sourceStackName] || []).length === 0) {
      return false;
    }

    return (
      (userStack[targetStackName] || []).length <
      (stackCapacities[targetStackName] || 0)
    );
  };

  const performMove = (sourceStackName: StackName, targetStackName: StackName) => {
    if (!canDropDisc(sourceStackName, targetStackName)) {
      return;
    }

    const sourceDiscs = userStack[sourceStackName] || [];
    const movedDisc = sourceDiscs[sourceDiscs.length - 1];
    if (!movedDisc) {
      return;
    }

    const nextUserStack = cloneStacks(userStack, stackNames);
    nextUserStack[sourceStackName] = nextUserStack[sourceStackName].slice(0, -1);
    nextUserStack[targetStackName] = [...nextUserStack[targetStackName], movedDisc];

    const currentTime = Date.now();
    const previousMoveTime = lastMoveTime ?? currentTime;
    const move: TowerMove = {
      from_stack: sourceStackName,
      disc: movedDisc,
      to_stack: targetStackName,
      total_time: Math.max(0, currentTime - previousMoveTime),
    };
    const nextMoves = [...moves, move];
    const didCompleteChallenge =
      stateKey(nextUserStack, stackNames) === stateKey(targetStack, stackNames);

    if (didCompleteChallenge) {
      const finalAnswer = buildTaskAnswer(
        targetStack,
        nextUserStack,
        nextMoves,
        stackNames
      );

      setUserStack(nextUserStack);
      setMoves(nextMoves);
      setSelectedSourceStack(null);
      setLastMoveTime(currentTime);
      setIsChallengeCompleted(true);
      setIsRoundLocked(false);
      setCompletionMoveCount(nextMoves.length);
      setPendingFinalAnswer(finalAnswer);
      setCompletionModalMode("countdown");
      setIsCompletionModalOpen(true);
      setCompletionRemainingMs(resolvedConfig.between_rounds_ms);
      return;
    }

    setUserStack(nextUserStack);
    setMoves(nextMoves);
    setSelectedSourceStack(null);
    setLastMoveTime(currentTime);
  };

  const startChallenge = () => {
    if (hasChallengeStarted || isStartingCountdown) {
      return;
    }

    setIsStartingCountdown(true);
    setCountdownValue(INTRO_COUNTDOWN_SECONDS);
  };

  React.useEffect(() => {
    if (!isStartingCountdown) {
      return;
    }

    if (countdownValue <= 1) {
      const startTimeout = window.setTimeout(() => {
        setHasChallengeStarted(true);
        setIsRoundLocked(true);
        setRoundUnlockRemainingMs(resolvedConfig.unlock_ms);
        setLastMoveTime(null);
        setIsStartingCountdown(false);
      }, 1000);

      return () => {
        window.clearTimeout(startTimeout);
      };
    }

    const timeoutId = window.setTimeout(() => {
      setCountdownValue((previousCountdownValue) => previousCountdownValue - 1);
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isStartingCountdown, countdownValue, resolvedConfig.unlock_ms]);

  React.useEffect(() => {
    if (
      !isCompletionModalOpen ||
      completionModalMode !== "countdown" ||
      !pendingFinalAnswer
    ) {
      return;
    }

    if (resolvedConfig.between_rounds_ms <= 0) {
      setIsCompletionModalOpen(false);
      setCompletionModalMode(null);
      setCompletionRemainingMs(0);
      onAnswerChange(pendingFinalAnswer);
      setPendingFinalAnswer(null);
      return;
    }

    const finishAt = Date.now() + resolvedConfig.between_rounds_ms;
    setCompletionRemainingMs(resolvedConfig.between_rounds_ms);

    const intervalId = window.setInterval(() => {
      setCompletionRemainingMs(Math.max(0, finishAt - Date.now()));
    }, TIMER_TICK_MS);

    const timeoutId = window.setTimeout(() => {
      setIsCompletionModalOpen(false);
      setCompletionModalMode(null);
      setCompletionRemainingMs(0);
      onAnswerChange(pendingFinalAnswer);
      setPendingFinalAnswer(null);
    }, resolvedConfig.between_rounds_ms);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [
    completionModalMode,
    isCompletionModalOpen,
    onAnswerChange,
    pendingFinalAnswer,
    resolvedConfig.between_rounds_ms,
  ]);

  React.useEffect(() => {
    if (!isRoundLocked || isChallengeCompleted) {
      return;
    }

    if (resolvedConfig.unlock_ms <= 0) {
      setIsRoundLocked(false);
      setRoundUnlockRemainingMs(0);
      setLastMoveTime(Date.now());
      return;
    }

    const unlockAt = Date.now() + resolvedConfig.unlock_ms;
    setRoundUnlockRemainingMs(resolvedConfig.unlock_ms);

    const intervalId = window.setInterval(() => {
      setRoundUnlockRemainingMs(Math.max(0, unlockAt - Date.now()));
    }, TIMER_TICK_MS);

    const timeoutId = window.setTimeout(() => {
      setIsRoundLocked(false);
      setRoundUnlockRemainingMs(0);
      setLastMoveTime(Date.now());
    }, resolvedConfig.unlock_ms);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [isRoundLocked, isChallengeCompleted, resolvedConfig.unlock_ms]);

  const onStackClick = (clickedStackName: StackName, isInteractive: boolean) => {
    if (!isInteractive) {
      return;
    }

    const clickedStackHasDisc = (userStack[clickedStackName] || []).length > 0;

    if (!selectedSourceStack) {
      if (!clickedStackHasDisc) {
        return;
      }
      setSelectedSourceStack(clickedStackName);
      return;
    }

    if (selectedSourceStack === clickedStackName) {
      setSelectedSourceStack(null);
      return;
    }

    if (canDropDisc(selectedSourceStack, clickedStackName)) {
      performMove(selectedSourceStack, clickedStackName);
      return;
    }

    setSelectedSourceStack(clickedStackHasDisc ? clickedStackName : null);
  };

  // ── Render helpers ───────────────────────────────────────────────────────────
  const stackHeight = React.useMemo(() => {
    const bottomOffset = 24;
    const safetyTopOffset = 20;
    const minHeight = 190;
    const maxStackCapacity = stackNames.reduce((highestCapacity, stackName) => {
      return Math.max(highestCapacity, stackCapacities[stackName] || 0);
    }, 1);

    const discsTotalHeight =
      maxStackCapacity * DISC_DIAMETER +
      Math.max(0, maxStackCapacity - 1) * DISC_GAP;

    return Math.max(minHeight, bottomOffset + discsTotalHeight + safetyTopOffset);
  }, [stackCapacities, stackNames]);

  const renderTower = (
    title: string,
    stacks: StackMap,
    isInteractive: boolean,
    isVisuallyDisabled: boolean = false,
    isInsideSharedContainer: boolean = false
  ) => (
    <Box
      width="100%"
      sx={{
        position: "relative",
        backgroundColor: isInsideSharedContainer ? "transparent" : "grey.100",
        borderRadius: isInsideSharedContainer ? 0 : "8px",
        padding: isInsideSharedContainer ? 0 : { xs: "1rem", md: "1.25rem" },
      }}
    >
      <Box
        sx={{
          opacity: isVisuallyDisabled ? 0.68 : 1,
          filter: isVisuallyDisabled ? "grayscale(20%)" : "none",
          transition: "opacity 0.2s ease, filter 0.2s ease",
        }}
      >
        <Typography
          variant="h6"
          color="primary"
          sx={{
            textAlign: "center",
            marginBottom: "0.5rem",
            userSelect: isInteractive ? "none" : "text",
            WebkitUserSelect: isInteractive ? "none" : "text",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: { xs: "0.75rem", md: "1rem" },
            flexWrap: "wrap",
          }}
        >
          {stackNames.map((stackName) => {
            const stack = stacks[stackName] || [];
            const isSourceStack = isInteractive && selectedSourceStack === stackName;
            const isMoveTarget =
              isInteractive &&
              selectedSourceStack !== null &&
              canDropDisc(selectedSourceStack, stackName);
            const stackCapacity = stackCapacities[stackName] || 1;
            const stackCapacityHeight = Math.min(
              stackHeight,
              getStackHeightByCapacity(stackCapacity)
            );
            const rodHeight = Math.max(
              56,
              stackCapacityHeight - ROD_BOTTOM_OFFSET - ROD_TOP_PADDING
            );

            const borderColor = isMoveTarget
              ? "rgba(30, 160, 151, 0.65)"
              : "rgba(0, 0, 0, 0.16)";
            const outline = isSourceStack ? "2px solid #1ea097" : "none";

            return (
              <Box
                key={stackName}
                onClick={() => onStackClick(stackName, isInteractive)}
                sx={{
                  width: { xs: 96, sm: 110 },
                  height: stackHeight,
                  boxSizing: "border-box",
                  border: `1px solid ${borderColor}`,
                  outline,
                  outlineOffset: "0px",
                  borderRadius: "10px",
                  position: "relative",
                  background: isInteractive
                    ? "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(245,247,250,0.98))"
                    : "linear-gradient(180deg, rgba(255,255,255,1), rgba(245,247,250,1))",
                  cursor: isInteractive ? "pointer" : "default",
                  transition: "border-color 0.2s ease, outline-color 0.2s ease",
                }}
              >
                  <Box
                    sx={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      bottom: ROD_BOTTOM_OFFSET,
                      width: 8,
                      height: rodHeight,
                      borderRadius: "99px",
                      background:
                        "linear-gradient(90deg, #5f6773 0%, #2c3440 45%, #66707c 100%)",
                      boxShadow:
                        "inset 1px 0 1px rgba(255,255,255,0.2), inset -1px 0 1px rgba(0,0,0,0.25)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      right: 10,
                      bottom: 10,
                      height: 12,
                      borderRadius: "8px",
                      background:
                        "linear-gradient(180deg, #6a7380 0%, #525c69 45%, #404955 100%)",
                      boxShadow:
                        "inset 0 1px 2px rgba(255,255,255,0.14), inset 0 -1px 2px rgba(0,0,0,0.28)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 24,
                      display: "flex",
                      flexDirection: "column-reverse",
                      alignItems: "center",
                      gap: `${DISC_GAP}px`,
                    }}
                  >
                    {stack.map((discColor, discIndex) => {
                      const isTopDisc = discIndex === stack.length - 1;
                      const isSelectedDisc = isInteractive && isTopDisc && isSourceStack;
                      const discVisualColor =
                        resolvedConfig.disc_color_map[discColor] || discColor;

                      return (
                        <Box
                          key={`${stackName}-${discColor}-${discIndex}`}
                          sx={{
                            width: DISC_DIAMETER,
                            height: DISC_DIAMETER,
                            borderRadius: "50%",
                            position: "relative",
                            overflow: "hidden",
                            background: `radial-gradient(circle at 30% 24%, rgba(255,255,255,0.62) 0%, rgba(255,255,255,0.14) 30%, rgba(255,255,255,0) 52%), radial-gradient(circle at 72% 80%, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0) 58%), ${discVisualColor}`,
                            border: "1px solid rgba(17, 24, 39, 0.22)",
                            boxSizing: "border-box",
                            transform: isSelectedDisc ? "scale(1.04)" : "scale(1)",
                            opacity: isInteractive && !isTopDisc ? 0.88 : 1,
                            cursor: isInteractive && isTopDisc ? "pointer" : "default",
                            boxShadow:
                              isSelectedDisc
                                ? "inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -3px 6px rgba(0,0,0,0.24), 0 0 0 2px rgba(30,160,151,0.35), 0 2px 3px rgba(0,0,0,0.15)"
                                : "inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -3px 6px rgba(0,0,0,0.24), 0 2px 3px rgba(0,0,0,0.15)",
                            transition:
                              "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              left: "50%",
                              top: "18%",
                              bottom: "18%",
                              width: 8,
                              transform: "translateX(-50%)",
                              borderRadius: "999px",
                              background:
                                "linear-gradient(180deg, rgba(18,25,34,0.34) 0%, rgba(255,255,255,0.08) 46%, rgba(18,25,34,0.34) 100%)",
                              opacity: 0.38,
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  const roundUnlockCountdownSeconds = Math.max(
    1,
    Math.ceil(roundUnlockRemainingMs / 1000)
  );
  const completionCountdownSeconds = Math.max(
    1,
    Math.ceil(completionRemainingMs / 1000)
  );
  const completionFeedbackTitle = resolvedConfig.training
    ? (resolvedConfig.feedback_text.trim().length > 0
        ? resolvedConfig.feedback_text
        : "Rodada concluída!")
    : null;

  return (
    <Box
      width="100%"
      marginTop={6}
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        gap: "1rem",
        maxWidth: 760,
      }}
    >
      {!hasChallengeStarted ? (
        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: "grey.100",
            borderRadius: "8px",
            padding: { xs: "1rem", md: "1.25rem" },
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="primary" sx={{ marginBottom: "0.75rem" }}>
            {towerConfig?.introduction?.title ?? INTRODUCTION_TITLE}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, marginBottom: "1rem" }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "left" }}>
              {towerConfig?.introduction?.text ?? INTRODUCTION_TEXT}
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                textAlign: "left",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Instruções
              </Typography>
              <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                {(towerConfig?.introduction?.instructions ?? DEFAULT_INTRODUCTION_INSTRUCTIONS).map((line: string, i: number) => (
                  <Box component="li" key={i} sx={{ marginBottom: "0.4rem" }}>
                    <Typography variant="body2" color="text.secondary">{line}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          {isStartingCountdown ? (
            <Typography variant="h6" color="primary">
              {`Começando em ${countdownValue}...`}
            </Typography>
          ) : (
            <Button variant="contained"
              sx={{
                borderRadius: 999,
                px: 3,
                py: 1,
                color: 'white'
              }}
              onClick={startChallenge}>
              {START_BUTTON_LABEL}
            </Button>
          )}
        </Box>
      ) : null}

      {hasChallengeStarted ? (
        (isLastRoundPosition || isTrainingLastPosition) && isChallengeCompleted && !isCompletionModalOpen ? (
        isTrainingLastPosition ? (
          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              backgroundColor: "grey.100",
              borderRadius: "8px",
              padding: { xs: "1rem", md: "1.25rem" },
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="primary" sx={{ marginBottom: "0.75rem" }}>
              {towerConfig?.training_finish?.title ?? DEFAULT_TRAINING_FINISH_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {towerConfig?.training_finish?.text ?? DEFAULT_TRAINING_FINISH_TEXT}
            </Typography>
          </Box>
        ) :
          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              backgroundColor: "grey.100",
              borderRadius: "8px",
              padding: { xs: "1rem", md: "1.25rem" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="primary">
              {completionFeedbackTitle ?? "Parabéns, tarefa concluída!"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Você concluiu o desafio das torres.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Clique em "Próximo" para continuar.
            </Typography>
          </Box>
        ) : (
        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: "grey.100",
            borderRadius: "8px",
            padding: { xs: "1rem", md: "1.25rem" },
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: "1.75rem",
          }}
        >
          {renderTower(
            "Configuração alvo",
            targetStack,
            false,
            isRoundLocked || isChallengeCompleted,
            true
          )}
          {renderTower(
            "Sua configuração",
            userStack,
            !isChallengeCompleted && !isRoundLocked,
            isRoundLocked || isChallengeCompleted,
            true
          )}
          {isRoundLocked ? (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 4,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  whiteSpace: "nowrap",
                }}
              >
                {roundUnlockCountdownSeconds <= 1 ? (
                  <LockOpenRoundedIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                ) : (
                  <LockRoundedIcon fontSize="small" color="action" />
                )}
                <Typography variant="body2" color="text.secondary">
                  {`Movimentação liberada em ${roundUnlockCountdownSeconds}s`}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
        )
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          Clique em 'Começar desafio' para exibir as configurações e iniciar a contagem de tempo.
        </Typography>
      )}

      <Modal open={isCompletionModalOpen}>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 360,
              backgroundColor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              p: 3,
              textAlign: "center",
            }}
          >
            {completionFeedbackTitle && (
              <Typography
                id="tower-complete-feedback-title"
                variant="h6"
                color="primary"
                sx={{ mb: 1 }}
              >
                {completionFeedbackTitle}
              </Typography>
            )}
            {resolvedConfig.training && completionMoveCount !== null ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {`Movimentações: ${completionMoveCount}`}
              </Typography>
            ) : null}
            {completionModalMode === "manual" ? (
              <Button
                variant="contained"
                sx={{ borderRadius: 999, px: 3, py: 1, color: "white" }}
                onClick={() => {
                  setIsCompletionModalOpen(false);
                  setCompletionModalMode(null);
                }}
              >
                Fechar
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {`Próxima questão em ${completionCountdownSeconds}s`}
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TowerQuestion;
